import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { authErrorMessage, signIn, signInWithEmail, useAuth } from '@/lib/platform';
import { Button } from '@/components/ui/button';
import { Field } from '@/components/ui/field';

export const Route = createFileRoute('/login')({
  component: LoginPage,
});

/**
 * Sign-in for the booking tool.
 *
 * Two providers, both on the transit Firebase project and both landing in the
 * `wayq` tenant:
 *   • Google  — the primary path for operators (inherits 2SV/passkeys).
 *   • Email   — a secondary operator path. It exists because Google's popup cannot
 *               be driven by an automated browser, which left the booking flow
 *               (wizard → payment → QR → cancel) impossible to test end to end.
 *
 * Neither is an authorisation decision: access is gated on the `role` custom claim
 * in routes/_authed.tsx, so an account without a role gets in nowhere regardless of
 * how it signed in. There is deliberately no sign-up — accounts are provisioned
 * out-of-band and granted a role with scripts/set-admin-claim.sh.
 */
function LoginPage() {
  const authState = useAuth();
  const navigate = useNavigate();
  const [error, setError] = useState('');
  const [loading, setLoading] = useState<'google' | 'email' | null>(null);
  const [showEmail, setShowEmail] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  useEffect(() => {
    if (authState.status === 'authenticated') {
      void navigate({ to: '/' });
    }
  }, [authState.status, navigate]);

  if (authState.status === 'authenticated') {
    return null;
  }

  async function handleGoogle() {
    setError('');
    setLoading('google');
    try {
      await signIn();
      // Redirect is handled by the effect above once auth state settles.
    } catch (err) {
      setError(authErrorMessage(err));
    } finally {
      setLoading(null);
    }
  }

  async function handleEmail(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    if (!email.trim() || !password) {
      setError('Enter both an email and a password.');
      return;
    }
    setLoading('email');
    try {
      await signInWithEmail(email, password);
    } catch (err) {
      setError(authErrorMessage(err));
    } finally {
      setLoading(null);
    }
  }

  return (
    <div className="wb-shell wb-shell--auth">
      <div className="wb-auth-card wb-card wb-card--pad wb-col wb-gap-16">
        <header className="wb-col wb-gap-6">
          <div className="wb-eyebrow">Internal booking tool</div>
          <h1 className="wb-h2">WayQ Tickets</h1>
          <p className="wb-sub">
            Metro and bus (TRV11), rides (TRV10) and entry passes (TRV14) on the ONDC network.
          </p>
        </header>

        {error && (
          <div className="wb-error" role="alert">
            {error}
          </div>
        )}

        <Button
          variant="primary"
          icon="user"
          onClick={() => void handleGoogle()}
          disabled={loading !== null}
        >
          {loading === 'google' ? 'Opening Google…' : 'Sign in with Google'}
        </Button>

        <div className="wb-or">or</div>

        {showEmail ? (
          <form className="wb-col wb-gap-12" onSubmit={(e) => void handleEmail(e)}>
            <Field
              label="Email"
              type="email"
              autoComplete="username"
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="operator@wayq.in"
            />
            <Field
              label="Password"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
            <Button variant="secondary" type="submit" disabled={loading !== null}>
              {loading === 'email' ? 'Signing in…' : 'Sign in'}
            </Button>
          </form>
        ) : (
          <Button variant="ghost" onClick={() => setShowEmail(true)} disabled={loading !== null}>
            Sign in with email
          </Button>
        )}

        <hr className="wb-divider" />

        <p className="wb-tiny">
          Requires an operator role on the transit backend. Bookings are made under the WayQ tenant
          and are separate from customer accounts.
        </p>
      </div>
    </div>
  );
}
